/**
 * Modal de gerenciamento de módulos para mesas
 */

import { useState, useEffect } from 'react'
import { useModules } from '@/hooks/useModules'
import { getAllModules } from '@/modules'
import type { ModuleManifest, TableModuleInfo } from '@/modules/types'

interface ModuleManagerDialogProps {
  tableId: string
  isOpen: boolean
  onClose: () => void
  onModuleActivated?: () => void
}

export function ModuleManagerDialog({
  tableId,
  isOpen,
  onClose,
  onModuleActivated
}: ModuleManagerDialogProps) {
  const {
    modules: installedModules,
    tableModules,
    isLoading,
    error,
    listAvailableModules,
    listTableModules,
    activateModule,
    deactivateModule
  } = useModules()

  const [selectedModule, setSelectedModule] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && tableId) {
      loadData()
    }
  }, [isOpen, tableId])

  const loadData = async () => {
    try {
      await Promise.all([
        listAvailableModules(),
        listTableModules(tableId)
      ])
    } catch (err) {
      console.error('Erro ao carregar módulos:', err)
    }
  }

  const handleActivate = async (moduleId: string) => {
    setIsProcessing(true)
    setActionError(null)

    try {
      await activateModule(tableId, moduleId)
      await listTableModules(tableId)
      setSelectedModule(null)
      onModuleActivated?.()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao ativar módulo'
      setActionError(errorMessage)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDeactivate = async (moduleId: string) => {
    if (!confirm('Deseja realmente desativar este módulo? As configurações serão mantidas.')) {
      return
    }

    setIsProcessing(true)
    setActionError(null)

    try {
      await deactivateModule(tableId, moduleId)
      await listTableModules(tableId)
      setSelectedModule(null)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao desativar módulo'
      setActionError(errorMessage)
    } finally {
      setIsProcessing(false)
    }
  }

  const isModuleActive = (moduleId: string): boolean => {
    return tableModules.some(tm => tm.module_id === moduleId && tm.is_active)
  }

  const getActiveTableModule = (moduleId: string): TableModuleInfo | undefined => {
    return tableModules.find(tm => tm.module_id === moduleId && tm.is_active)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold">Gerenciar Módulos</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                <p className="mt-4 text-gray-600">Carregando módulos...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          ) : (
            <>
              {actionError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {actionError}
                </div>
              )}

              <div className="space-y-4">
                {installedModules.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    Nenhum módulo disponível no momento
                  </p>
                ) : (
                  installedModules.map((module) => {
                    const isActive = isModuleActive(module.id)
                    const tableModule = getActiveTableModule(module.id)

                    return (
                      <div
                        key={module.id}
                        className={`border rounded-lg p-4 transition-all ${
                          isActive
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold">{module.name}</h3>
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                v{module.version}
                              </span>
                              {isActive && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-medium">
                                  Ativo
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-3">
                              {module.description}
                            </p>
                            {isActive && tableModule && (
                              <p className="text-xs text-gray-500">
                                Ativado em:{' '}
                                {tableModule.activated_at ? new Date(tableModule.activated_at).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'Data não disponível'}
                              </p>
                            )}
                          </div>

                          {/* Action Button */}
                          <div>
                            {isActive ? (
                              <button
                                onClick={() => handleDeactivate(module.id)}
                                disabled={isProcessing}
                                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isProcessing ? 'Processando...' : 'Desativar'}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleActivate(module.id)}
                                disabled={isProcessing}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isProcessing ? 'Processando...' : 'Ativar'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
